import React, { useState, useEffect } from 'react';
import api from '../api/axios';
import { useConfig } from '../context/ConfigContext';

const CACHE_BUSTER = Date.now();

const Agenda = () => {
    const [events, setEvents] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [activeGroup, setActiveGroup] = useState(null);
    const [activeSubgroup, setActiveSubgroup] = useState(null);
    const { config } = useConfig();

    useEffect(() => {
        fetchAgenda();
    }, []);

    useEffect(() => {
        if (events.length > 0) {
            const availableGroups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
            if (availableGroups.length > 0 && !activeGroup) {
                setActiveGroup(availableGroups[0]);
            }
        }
    }, [events, activeGroup]);

    useEffect(() => {
        if (events.length > 0 && activeGroup) {
            const activeGroupEvents = events.filter(e => e.group_name === activeGroup);
            const availableSubgroups = Array.from(new Set(activeGroupEvents.map(e => e.subgroup_name).filter(Boolean)));
            if (availableSubgroups.length > 0) {
                setActiveSubgroup(prev => {
                    if (!prev || !availableSubgroups.includes(prev)) {
                        return availableSubgroups[0];
                    }
                    return prev;
                });
            } else {
                setActiveSubgroup(null);
            }
        }
    }, [events, activeGroup]);

    const fetchAgenda = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/agenda?status=active');
            setEvents(res.data);
        } catch (error) {
            console.error("Error fetching agenda", error);
        } finally {
            setIsLoading(false);
        }
    };

    const pageDesign = config?.pageDesigns?.agenda || {};
    const isImageOnly = pageDesign.agendaImageOnly;

    // Helper functions for flags
    const getFlagEmoji = (country) => {
        if (!country) return '';
        const name = country.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "");
        const flags = {
            'colombia': '🇨🇴', 'co': '🇨🇴',
            'mexico': '🇲🇽', 'mx': '🇲🇽',
            'argentina': '🇦🇷', 'ar': '🇦🇷',
            'chile': '🇨🇱', 'cl': '🇨🇱',
            'peru': '🇵🇪', 'pe': '🇵🇪',
            'ecuador': '🇪🇨', 'ec': '🇪🇨',
            'espana': '🇪🇸', 'es': '🇪🇸',
            'estados unidos': '🇺🇸', 'usa': '🇺🇸', 'us': '🇺🇸',
            'brasil': '🇧🇷', 'brazil': '🇧🇷', 'br': '🇧🇷',
            'venezuela': '🇻🇪', 've': '🇻🇪',
            'uruguay': '🇺🇾', 'uy': '🇺🇾',
            'paraguay': '🇵🇾', 'py': '🇵🇾',
            'bolivia': '🇧🇴', 'bo': '🇧🇴',
            'panama': '🇵🇦', 'pa': '🇵🇦',
            'costa rica': '🇨🇷', 'cr': '🇨🇷',
            'cuba': '🇨🇺', 'cu': '🇨🇺',
            'republica dominicana': '🇩🇴', 'do': '🇩🇴',
            'puerto rico': '🇵🇷', 'pr': '🇵🇷',
            'honduras': '🇭🇳', 'hn': '🇭🇳',
            'el salvador': '🇸🇻', 'sv': '🇸🇻',
            'guatemala': '🇬🇹', 'gt': '🇬🇹',
            'nicaragua': '🇳🇮', 'ni': '🇳🇮',
            'italia': '🇮🇹', 'it': '🇮🇹',
            'francia': '🇫🇷', 'fr': '🇫🇷',
            'alemania': '🇩🇪', 'de': '🇩🇪',
            'reino unido': '🇬🇧', 'uk': '🇬🇧', 'gb': '🇬🇧',
            'canada': '🇨🇦', 'ca': '🇨🇦'
        };
        return flags[name] || '';
    };

    const groups = Array.from(new Set(events.map(e => e.group_name).filter(Boolean)));
    const activeGroupEvents = activeGroup ? events.filter(e => e.group_name === activeGroup) : events;
    let subgroups = Array.from(new Set(activeGroupEvents.map(e => e.subgroup_name).filter(Boolean)));
    if (pageDesign?.subgroupOrder) {
        const orderArr = pageDesign.subgroupOrder.split(',').map(s => s.trim().toUpperCase());
        subgroups = subgroups.sort((a, b) => {
            const indexA = orderArr.indexOf(a.toUpperCase());
            const indexB = orderArr.indexOf(b.toUpperCase());
            if (indexA !== -1 && indexB !== -1) return indexA - indexB;
            if (indexA !== -1) return -1;
            if (indexB !== -1) return 1;
            return a.localeCompare(b);
        });
    }

    const filteredEvents = events.filter(e => {
        const matchesGroup = !activeGroup || e.group_name === activeGroup;
        const matchesSubgroup = !activeSubgroup || e.subgroup_name === activeSubgroup;
        return matchesGroup && matchesSubgroup;
    });

    const getActiveLegalConfig = (group, subgroup) => {
        const glts = pageDesign.groupLegalTexts || [];
        const normalize = (str) => (str || '').trim().toLowerCase();

        const nGroup = normalize(group);
        const nSub = normalize(subgroup);

        let match = glts.find(g => normalize(g.groupName) === nGroup && normalize(g.subgroupName) === nSub);
        if (match && match.legalTextUrl) return match;

        match = glts.find(g => normalize(g.groupName) === nGroup && !normalize(g.subgroupName));
        if (match && match.legalTextUrl) return match;

        if (nSub) {
            match = glts.find(g => !normalize(g.groupName) && normalize(g.subgroupName) === nSub);
            if (match && match.legalTextUrl) return match;
        }

        return {
            showLegalText: pageDesign.showLegalText,
            legalTextUrl: pageDesign.legalTextUrl
        };
    };

    // Group events by date and block
    const groupedData = filteredEvents.reduce((acc, event) => {
        const dateStr = new Date(event.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
        const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);

        if (!acc[capitalizedDate]) acc[capitalizedDate] = {};

        const block = event.block_name || 'Sin Bloque';
        if (!acc[capitalizedDate][block]) acc[capitalizedDate][block] = {
            name: block,
            color: event.block_color || '#0a58ca',
            events: []
        };

        const existingEvent = acc[capitalizedDate][block].events.find(e => e.title === event.title && e.start_time === event.start_time);

        if (existingEvent) {
            if (event.speaker_name) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.name === event.speaker_name)) {
                    existingEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                }
            } else if (event.country) {
                existingEvent.speakers = existingEvent.speakers || [];
                if (!existingEvent.speakers.some(s => s.country === event.country)) {
                    existingEvent.speakers.push({ name: '', country: event.country });
                }
            }
        } else {
            const newEvent = { ...event, speakers: [] };
            if (event.speaker_name) {
                newEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
            } else if (event.country) {
                newEvent.speakers.push({ name: '', country: event.country });
            }
            acc[capitalizedDate][block].events.push(newEvent);
        }

        return acc;
    }, {});

    const getIcon = (type) => {
        if (type === 'breakfast') return '🥐';
        if (type === 'coffee') return '☕';
        if (type === 'lunch') return '🍽️';
        if (type === 'closing') return '✈️';
        if (type === 'other') return '✨';
        return null;
    };

    // Determine the active visual theme colors based on group/subgroup for absolute fidelity
    const isTepezzaActive = activeSubgroup === "TEPEZZA" || activeGroup === "TEPEZZA";
    const primaryThemeColor = isTepezzaActive ? "#5bb343" : "#0a58ca"; // Green for TEPEZZA, Corporate Blue otherwise
    const secondaryThemeColor = "#ffa800"; // Orange/Yellow

    // Dynamically retrieve font colors from admin config for groups and subgroups
    const activeSubgroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => 
        (g.groupName === activeGroup && g.subgroupName === activeSubgroup) || 
        g.groupName === activeSubgroup
    );
    const activeGroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => g.groupName === activeGroup && !g.subgroupName);

    // Try to find a config for the subgroup if it exists, otherwise fallback to group
    const dynamicGroupFontColor = activeSubgroupConfig?.globalFontColor || activeSubgroupConfig?.primaryColor || activeGroupConfig?.globalFontColor || activeGroupConfig?.primaryColor || primaryThemeColor;

    // We will use dynamicGroupFontColor for the texts in the agenda
    const finalFontColor = pageDesign.globalFontColor || dynamicGroupFontColor;

    // Inject the final font color as a CSS variable so Header.js can read it for the main title
    React.useEffect(() => {
        document.documentElement.style.setProperty('--agenda-font-color', finalFontColor);
        return () => {
            document.documentElement.style.removeProperty('--agenda-font-color');
        };
    }, [finalFontColor]);

    const imageAgendas = pageDesign.imageAgendas || [];

    if (isImageOnly) {
        if (imageAgendas.length > 0) {
            const currentActiveGroup = activeGroup || imageAgendas[0]?.group_name;
            const activeGroupItems = imageAgendas.filter(a => a.group_name === currentActiveGroup);
            
            const sortSubgroups = (subgroupsArray) => {
                if (!pageDesign.subgroupOrder) return subgroupsArray;
                const orderArr = pageDesign.subgroupOrder.split(',').map(s => s.trim().toUpperCase());
                return subgroupsArray.sort((a, b) => {
                    const indexA = orderArr.indexOf(a.toUpperCase());
                    const indexB = orderArr.indexOf(b.toUpperCase());
                    if (indexA !== -1 && indexB !== -1) return indexA - indexB;
                    if (indexA !== -1) return -1;
                    if (indexB !== -1) return 1;
                    return a.localeCompare(b);
                });
            };

            const imageSubgroups = sortSubgroups(Array.from(new Set(activeGroupItems.map(a => a.subgroup_name).filter(Boolean))));

            // Auto-select first subgroup if none is active but subgroups exist for this group
            let currentActiveSubgroup = activeSubgroup;
            if (imageSubgroups.length > 0 && !imageSubgroups.includes(currentActiveSubgroup)) {
                currentActiveSubgroup = imageSubgroups[0];
            } else if (imageSubgroups.length === 0) {
                currentActiveSubgroup = null;
            }

            const activeImageItem = activeGroupItems.find(a =>
                (currentActiveSubgroup ? a.subgroup_name === currentActiveSubgroup : true)
            ) || activeGroupItems[0];
            const activeImage = activeImageItem?.staticImage || imageAgendas[0]?.staticImage;

            const uniqueGroups = Array.from(new Set(imageAgendas.map(a => a.group_name).filter(Boolean)));

            return (
                <div className="animate-fadeIn max-w-5xl mx-auto font-sans pt-4 sm:pt-6">
                    <div className="flex flex-col items-center gap-4 mb-1 pb-1">
                        <div className="flex justify-center gap-4 w-full overflow-x-auto pb-2 px-4 custom-scrollbar">
                            {uniqueGroups.map(group => {
                                const isSelected = currentActiveGroup === group;
                                return (
                                    <button
                                        key={group}
                                        onClick={() => {
                                            setActiveGroup(group);
                                            // Subgroup auto-select handles itself in the render logic above when activeGroup changes
                                        }}
                                        className={`px-8 py-3 rounded-lg font-black uppercase tracking-widest text-[12px] sm:text-[14px] transition-all whitespace-nowrap shadow-md ${isSelected
                                            ? `bg-[#ffa800] text-white`
                                            : 'bg-[#0a58ca] text-white hover:opacity-90'
                                            }`}
                                    >
                                        {group}
                                    </button>
                                );
                            })}
                        </div>

                        {pageDesign.subgroupPosition !== 'bottom' && imageSubgroups.length > 0 && (
                            <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-2">
                                {imageSubgroups.map(subg => {
                                    const isSelected = currentActiveSubgroup === subg;
                                    return (
                                        <button
                                            key={subg}
                                            onClick={() => setActiveSubgroup(subg)}
                                            style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                            className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                                        >
                                            {subg}
                                        </button>
                                    );
                                })}
                            </div>
                        )}

                        {(() => {
                            const downloadUrl = activeImageItem?.downloadUrl || activeGroupItems.find(a => a.downloadUrl)?.downloadUrl;
                            if (downloadUrl) {
                                return (
                                    <a
                                        href={downloadUrl}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="bg-slate-100 text-slate-800 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all border border-slate-300 shadow-md flex items-center gap-2 mt-2"
                                    >
                                        <span>⬇️</span> Descargar Agenda
                                    </a>
                                );
                            }
                            return null;
                        })()}
                    </div>
                    <div className="flex flex-col items-center p-0 bg-transparent w-full">
                        {activeImage ? (
                            <img
                                src={`${activeImage}${activeImage.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                                alt={`Agenda ${currentActiveGroup} ${currentActiveSubgroup || ''}`}
                                className="w-full max-w-full h-auto max-h-[85vh] object-contain shadow-2xl mb-10 mx-auto rounded-xl"
                            />
                        ) : (
                            <div className="text-slate-400 py-10 uppercase tracking-widest font-black text-xs">Sin imagen configurada</div>
                        )}
                    </div>
                    {pageDesign.subgroupPosition === 'bottom' && imageSubgroups.length > 0 && (
                        <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-6 mb-4">
                            {imageSubgroups.map(subg => {
                                const isSelected = currentActiveSubgroup === subg;
                                return (
                                    <button
                                        key={subg}
                                        onClick={() => setActiveSubgroup(subg)}
                                        style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                        className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                                    >
                                        {subg}
                                    </button>
                                );
                            })}
                        </div>
                    )}
                    {(() => {
                        const legalConfig = getActiveLegalConfig(currentActiveGroup, currentActiveSubgroup);
                        return legalConfig?.showLegalText && legalConfig?.legalTextUrl && (
                            <footer className="w-full p-4 sm:p-6 mt-auto flex justify-center items-center z-40 select-none bg-transparent">
                                {/\.(pdf)$/i.test(legalConfig.legalTextUrl) ? (
                                    <a href={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold text-sm">Ver Información Legal</a>
                                ) : (
                                    <img
                                        src={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                                        alt="Información Legal"
                                        className="max-w-[90vw] md:max-w-2xl h-auto object-contain pointer-events-auto"
                                    />
                                )}
                            </footer>
                        );
                    })()}
                </div>
            );
        }
    }

    return (
        <div className="animate-fadeIn max-w-4xl mx-auto pb-32 font-sans pt-4 sm:pt-6">
            {/* The Floating elements configured in apply_amgen_designs.js are automatically renderered by Layout.js above */}

            {isLoading ? (
                <div className="flex flex-col items-center justify-center py-20">
                    <div className="w-12 h-12 border-4 border-slate-200 border-t-[#0a58ca] rounded-full animate-spin"></div>
                    <p className="text-slate-400 text-[10px] font-black uppercase tracking-[0.4em] mt-6 animate-pulse">Cargando Agenda...</p>
                </div>
            ) : (
                <div className="space-y-12 px-4 sm:px-6">
                    {/* 1. Header Centrado: Título Agenda */}
                    <div className="flex flex-col items-center justify-center text-center mt-2 mb-6 select-none font-sans">
                        <h1 className="text-3xl sm:text-5xl font-black text-[#c20042] tracking-tight leading-tight mb-2 drop-shadow-md">
                            {pageDesign.title || 'AGENDA ACADÉMICA'}
                        </h1>
                        <span className="text-sm sm:text-base font-bold text-white/80">
                            {pageDesign.location || pageDesign.subtitle || 'Cardio Summit 2026'}
                        </span>
                    </div>

                    {/* 2. Toggles / Tabs de Días (DÍA 1, DÍA 2) */}
                    {groups.length > 0 && (
                        <div className="flex flex-col items-center gap-4 mb-8">
                            <div className="flex justify-center gap-6 w-full overflow-x-auto pb-2 px-4 custom-scrollbar">
                                {groups.map(group => {
                                    const isSelected = activeGroup === group;
                                    return (
                                        <button
                                            key={group}
                                            onClick={() => setActiveGroup(group)}
                                            style={{
                                                backgroundColor: isSelected ? "#c20042" : "rgba(129, 6, 145, 0.6)",
                                                border: isSelected ? "2px solid #ffffff" : "1px solid rgba(255,255,255,0.2)"
                                            }}
                                            className="px-8 py-3 rounded-2xl font-black uppercase tracking-widest text-[13px] sm:text-[15px] text-white transition-all shadow-lg hover:scale-[1.02] active:scale-95 duration-200 min-w-[140px] text-center"
                                        >
                                            {group}
                                        </button>
                                    );
                                })}
                            </div>

                            {/* Botón Descargar Agenda */}
                            {(() => {
                                const activeSubgroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => g.groupName === activeGroup && g.subgroupName === activeSubgroup);
                                const activeGroupConfig = config?.pageDesigns?.agenda?.groupColors?.find(g => g.groupName === activeGroup && !g.subgroupName);
                                
                                let downloadUrl = activeSubgroupConfig?.downloadUrl || activeGroupConfig?.downloadUrl;
                                
                                if (!downloadUrl && config?.pageDesigns?.agenda?.imageAgendas) {
                                    const fallbackSub = config.pageDesigns.agenda.imageAgendas.find(a => a.group_name === activeGroup && a.subgroup_name === activeSubgroup && a.downloadUrl);
                                    const fallbackGroup = config.pageDesigns.agenda.imageAgendas.find(a => a.group_name === activeGroup && !a.subgroup_name && a.downloadUrl);
                                    if (fallbackSub) downloadUrl = fallbackSub.downloadUrl;
                                    else if (fallbackGroup) downloadUrl = fallbackGroup.downloadUrl;
                                }

                                if (downloadUrl) {
                                    return (
                                        <a
                                            href={downloadUrl}
                                            target="_blank"
                                            rel="noopener noreferrer"
                                            className="bg-slate-100 text-slate-800 px-6 py-3 rounded-full text-[10px] font-black uppercase tracking-widest hover:bg-slate-200 transition-all border border-slate-300 shadow-md flex items-center gap-2"
                                        >
                                            <span>⬇️</span> Descargar Agenda
                                        </a>
                                    );
                                }
                                return null;
                            })()}

                            {/* Subgrupos / Laboratorios */}
                            {pageDesign.subgroupPosition !== 'bottom' && subgroups.length > 0 && (
                                <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-2">
                                    {subgroups.map(subg => {
                                        const isSelected = activeSubgroup === subg;
                                        return (
                                            <button
                                                key={subg}
                                                onClick={() => setActiveSubgroup(subg)}
                                                style={{
                                                    backgroundColor: isSelected ? "#810691" : "rgba(85, 0, 67, 0.6)",
                                                    border: isSelected ? "2px solid #ffffff" : "1px solid rgba(255,255,255,0.2)"
                                                }}
                                                className="px-6 py-2.5 rounded-xl font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[120px] text-center"
                                            >
                                                {subg}
                                            </button>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {Object.keys(groupedData).map((date, dayIdx) => {
                        // Gather all events for this date, deduplicating speakers for identical title + start_time
                        const dateEvents = filteredEvents.filter(e => {
                            const dateStr = new Date(e.date).toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' });
                            const capitalizedDate = dateStr.charAt(0).toUpperCase() + dateStr.slice(1);
                            return capitalizedDate === date;
                        });

                        // Deduplicate events by title and start_time while preserving original order
                        const uniqueEvents = [];
                        dateEvents.forEach(event => {
                            const existing = uniqueEvents.find(e => e.title === event.title && e.start_time === event.start_time);
                            if (existing) {
                                if (event.speaker_name) {
                                    existing.speakers = existing.speakers || [];
                                    if (!existing.speakers.some(s => s.name === event.speaker_name)) {
                                        existing.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                                    }
                                } else if (event.country) {
                                    existing.speakers = existing.speakers || [];
                                    if (!existing.speakers.some(s => s.country === event.country)) {
                                        existing.speakers.push({ name: '', country: event.country });
                                    }
                                }
                            } else {
                                const newEvent = { ...event, speakers: [] };
                                if (event.speaker_name) {
                                    newEvent.speakers.push({ name: event.speaker_name, country: event.speaker_country || event.country });
                                } else if (event.country) {
                                    newEvent.speakers.push({ name: '', country: event.country });
                                }
                                uniqueEvents.push(newEvent);
                            }
                        });

                        // Keep track of rendered block names and moderators to display block headers chronologically
                        let currentBlockName = null;
                        let currentModerator = null;

                        return (
                            <div key={dayIdx} className="space-y-6">
                                {/* 3. Título de la Fecha (Día 1 / Fecha) */}
                                <div className="w-full text-left my-4 pl-1 select-none font-sans">
                                    <h2 className="text-3xl sm:text-4xl font-extrabold text-[#ce0f46] leading-tight">
                                        {activeGroup ? activeGroup : `Día ${dayIdx + 1}`}
                                    </h2>
                                    <h3 className="text-xl sm:text-2xl font-extrabold text-white capitalize leading-tight">
                                        {date}
                                    </h3>
                                </div>

                                {/* Table Column Header — Estricto 3 columnas en Móviles & Desktop */}
                                <div
                                    className="flex w-full gap-1.5 sm:gap-4 mb-3 mt-4 px-2 sm:px-4 py-2.5 sm:py-3 select-none items-center rounded-2xl shadow-md text-white border border-white/20"
                                    style={{
                                        background: 'linear-gradient(135deg, #c20042, #810691)'
                                    }}
                                >
                                    <div className="w-[28%] sm:w-44 shrink-0 font-black text-center text-[10px] sm:text-sm uppercase tracking-wider sm:tracking-widest flex items-center justify-center gap-1">
                                        <span>🕒</span> <span className="truncate">Hora</span>
                                    </div>
                                    <div className="flex-1 min-w-0 font-black text-left pl-1 sm:pl-4 text-[10px] sm:text-sm uppercase tracking-wider sm:tracking-widest flex items-center justify-start gap-1">
                                        <span>💬</span> <span className="truncate">Tema</span>
                                    </div>
                                    <div className="w-[34%] sm:w-60 shrink-0 font-black text-left pl-1 sm:pl-3 text-[10px] sm:text-sm uppercase tracking-wider sm:tracking-widest flex items-center justify-start gap-1">
                                        <span>👤</span> <span className="truncate">Conferencista</span>
                                    </div>
                                </div>

                                {/* Chronological events list */}
                                <div className="space-y-3">
                                    {uniqueEvents.map((event, i) => {
                                        const icon = getIcon(event.activity_type);
                                        const isSpecial = event.activity_type !== 'session';
                                        const blockName = event.block_name;
                                        const moderator = event.moderator;

                                        const showBlockHeader = blockName && (blockName !== currentBlockName || moderator !== currentModerator);
                                        if (showBlockHeader) {
                                            currentBlockName = blockName;
                                            currentModerator = moderator;
                                        }

                                        return (
                                            <React.Fragment key={i}>
                                                {/* Block Header + Moderator combined inside the same pill capsule */}
                                                {showBlockHeader && (
                                                    <div className="w-full my-4">
                                                        <div
                                                            className="w-full py-2.5 px-4 sm:px-6 rounded-2xl font-bold text-center text-white shadow-md border border-white/20 flex flex-col items-center justify-center leading-snug"
                                                            style={{
                                                                backgroundColor: event.block_color || '#c20042'
                                                            }}
                                                        >
                                                            <div className="text-xs sm:text-base font-extrabold tracking-wide">
                                                                {blockName}
                                                            </div>
                                                            {moderator && (
                                                                <div className="text-[10px] sm:text-sm font-medium tracking-normal text-white/95 mt-0.5">
                                                                    <span className="font-bold">Moderador:</span> {moderator}
                                                                </div>
                                                            )}
                                                        </div>
                                                    </div>
                                                )}

                                                {isSpecial ? (
                                                    <div
                                                        className="w-full py-3.5 sm:py-5 px-3 sm:px-8 rounded-2xl shadow-xl flex flex-row items-center justify-center gap-2 sm:gap-4 text-white font-extrabold text-xs sm:text-xl uppercase tracking-wider my-3 border border-white/30 transition-all hover:scale-[1.01]"
                                                        style={{
                                                            background: 'linear-gradient(to right, #c20042, #810691)'
                                                        }}
                                                    >
                                                        <span className="text-lg sm:text-3xl">{icon || '✨'}</span>
                                                        <span className="font-black text-xs sm:text-2xl whitespace-nowrap">{event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}</span>
                                                        <span className="mx-1 sm:mx-2 font-black text-xs sm:text-2xl">•</span>
                                                        <span className="font-black text-xs sm:text-xl break-words min-w-0 flex-1 text-center">{event.title}</span>
                                                    </div>
                                                ) : (
                                                    <div
                                                        className="w-full py-3 sm:py-4 px-2 sm:px-6 rounded-2xl shadow-md border border-white/20 flex flex-row items-center gap-1.5 sm:gap-6 transition-all duration-300 hover:shadow-lg"
                                                        style={{
                                                            background: 'linear-gradient(90deg, rgba(85, 0, 67, 0.85), rgba(129, 6, 145, 0.75))',
                                                            backdropFilter: 'blur(8px)'
                                                        }}
                                                    >
                                                        {/* Hour Capsule — Columna 1 */}
                                                        <div className="w-[28%] sm:w-44 shrink-0 flex justify-center items-center">
                                                            <div className="px-1.5 sm:px-4 py-1.5 sm:py-2 text-white font-black text-[10px] sm:text-base text-center tracking-tight select-none flex items-center justify-center shadow-md w-full rounded-xl bg-black/25 border border-white/20 leading-tight">
                                                                {event.start_time.slice(0, 5)} - {event.end_time.slice(0, 5)}
                                                            </div>
                                                        </div>

                                                        {/* Intervention Topic Title — Columna 2 */}
                                                        <div className="flex-1 min-w-0 text-left pl-1 sm:pl-3">
                                                            <h3 className="text-xs sm:text-xl leading-tight sm:leading-snug font-extrabold text-white break-words">
                                                                {event.title}
                                                            </h3>
                                                        </div>

                                                        {/* Speakers list — Columna 3 */}
                                                        <div className="w-[34%] sm:w-60 shrink-0 flex items-center justify-start pl-1 sm:pl-3 min-w-0">
                                                            <div className="flex flex-col gap-1 text-left w-full min-w-0">
                                                                {event.speakers.map((speaker, idx) => (
                                                                    <div key={idx} className="flex flex-row items-center justify-start gap-1 w-full min-w-0">
                                                                        <span className="text-[10px] sm:text-lg font-bold sm:font-black text-white leading-tight break-words flex-1 min-w-0">
                                                                            {speaker.name}
                                                                        </span>
                                                                        {speaker.country && (
                                                                            <span className="text-xs sm:text-xl filter drop-shadow-sm select-none shrink-0" title={speaker.country}>
                                                                                {getFlagEmoji(speaker.country)}
                                                                            </span>
                                                                        )}
                                                                    </div>
                                                                ))}
                                                            </div>
                                                        </div>
                                                    </div>
                                                )}
                                            </React.Fragment>
                                        );
                                    })}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {pageDesign.subgroupPosition === 'bottom' && subgroups.length > 0 && (
                <div className="flex justify-center gap-3 w-full overflow-x-auto pb-2 px-4 custom-scrollbar mt-10 mb-4">
                    {subgroups.map(subg => {
                        const isSelected = activeSubgroup === subg;
                        return (
                            <button
                                key={subg}
                                onClick={() => setActiveSubgroup(subg)}
                                style={{ backgroundColor: isSelected ? "#ffa800" : "#0a58ca" }}
                                className="px-6 py-2.5 rounded-lg font-black uppercase tracking-widest text-[11px] sm:text-[12px] text-white transition-all shadow-md hover:scale-[1.02] active:scale-95 duration-200 min-w-[110px] text-center"
                            >
                                {subg}
                            </button>
                        );
                    })}
                </div>
            )}

            {(() => {
                const legalConfig = getActiveLegalConfig(activeGroup, activeSubgroup);
                return legalConfig?.showLegalText && legalConfig?.legalTextUrl && (
                    <footer className="w-full p-4 sm:p-6 mt-auto flex justify-center items-center z-40 select-none bg-transparent">
                        {/\.(pdf)$/i.test(legalConfig.legalTextUrl) ? (
                            <a href={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline font-bold text-sm">Ver Información Legal</a>
                        ) : (
                            <img
                                src={`${legalConfig.legalTextUrl}${legalConfig.legalTextUrl.includes('?') ? '&' : '?'}v=${CACHE_BUSTER}`}
                                alt="Información Legal"
                                className="max-w-[90vw] md:max-w-2xl h-auto object-contain pointer-events-auto"
                            />
                        )}
                    </footer>
                );
            })()}
        </div>
    );
};

export default Agenda;
